import { Link } from "wouter";
import { toast } from "sonner";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import { SuggestionRow } from "@/components/origination/SuggestionRow";
import { useSuggestions } from "@/hooks/useSuggestions";
import { SPECIALTY_LABELS } from "@/components/coi/specialty";
import { ORIGINATION_SOURCE_LABELS } from "@shared/originationConstants";
import { companyHref, type BdPayload } from "@shared/bdTracker";
import { StageBadge } from "./StageBadge";
import { plural } from "./format";

/** Who introduced us to whom this year, and what each became. Intros the detector found wait on top for a person to confirm. */
export function BdReferrers({ data }: { data: BdPayload }) {
  const { data: suggestions = [], accept, dismiss } = useSuggestions({});
  const incoming = suggestions.filter((s) => s.direction === "in");
  return (
    <WidgetPanel title="Who's sending us business" meta="YTD">
      {incoming.length > 0 && (
        <div className="border-b border-border/60 pb-1">
          <p className="px-3 pt-2 text-[11px] font-medium text-muted-foreground">{`${plural(incoming.length, "intro")} to confirm`}</p>
          {incoming.map((s) => (
            <div key={s.id} className="px-2 py-1">
              <SuggestionRow s={s} pending={accept.isPending || dismiss.isPending}
                onAccept={() => accept.mutate(s.id, { onSuccess: () => toast.success("Attributed") })}
                onDismiss={() => dismiss.mutate(s.id)} />
            </div>
          ))}
        </div>
      )}
      {data.referrers.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">No referrals yet this year. When a COI introduces someone, create the prospect from their page and it lands here.</p>
      ) : data.referrers.map((r) => {
        const kind = r.specialty ? SPECIALTY_LABELS[r.specialty] ?? r.specialty : ((ORIGINATION_SOURCE_LABELS as Record<string, string>)[r.sourceType] ?? r.sourceType);
        return (
          <div key={r.contactId ?? r.name} className="px-3 py-2 border-b border-border/40 text-xs">
            <div className="flex items-baseline justify-between gap-2">
              {r.contactId
                ? <Link href={`/coi/${r.contactId}`} className="font-medium text-sm hover:underline truncate">{r.name}</Link>
                : <span className="font-medium text-sm truncate">{r.name}</span>}
              <span className="text-muted-foreground truncate">{[r.firmName, kind].filter(Boolean).join(" · ")}</span>
            </div>
            {r.companies.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 mt-1 pl-2 border-l-2 border-border">
                <Link href={companyHref(c.category, c.id)} className="truncate hover:underline">{c.name}</Link>
                <StageBadge c={c} />
              </div>
            ))}
          </div>
        );
      })}
      <p className="px-3 py-2 text-xs text-muted-foreground">Referrals we sent back: <b>{data.tiles.referralsSentYtd}</b> this year.</p>
    </WidgetPanel>
  );
}
